import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth
  const [org, setOrg] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(null); // null=unknown, bool

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrg(data.organization);
    } catch {
      setUser(false);
    }
  }, []);

  const boot = useCallback(async () => {
    try {
      const { data } = await api.get("/setup/status");
      setNeedsSetup(data.needs_setup);
      if (data.needs_setup) { setUser(false); return; }
    } catch {
      setNeedsSetup(false);
    }
    // No localStorage flag — rely on the httpOnly cookie; a 401 means "not logged in".
    await load();
  }, [load]);

  useEffect(() => { boot(); }, [boot]);

  const login = useCallback(async (email, password) => {
    await api.post("/auth/login", { email, password }); // backend sets httpOnly cookie
    await load();
  }, [load]);

  const setup = useCallback(async (payload) => {
    await api.post("/setup", payload); // backend sets httpOnly cookie
    setNeedsSetup(false);
    await load();
  }, [load]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setOrg(null);
  }, []);

  const value = useMemo(
    () => ({ user, org, setOrg, needsSetup, login, setup, logout, reload: load }),
    [user, org, needsSetup, login, setup, logout, load]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
